// An accepted option is locked (DD, 2026-10-13): its price is on the CRM
// opportunity. Unlock (only while the opportunity is open) → change → Accept
// again, which re-prices it, rewrites the opportunity's lines and locks it.
import { useState } from 'react';
import { Lock, LockOpen } from 'lucide-react';
import { unlockOptionApi } from '../lib/api';
import type { DeviceRecord } from '../lib/device-db';
import { formatDate } from '../lib/dates';
import { ErrorMessage } from './message';

const day = (value: unknown) => formatDate(value) ?? 'an earlier day';

export function OptionLockBanner({ option, onUnlocked }: { option: DeviceRecord; onUnlocked: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const accepted = option.fields.hig_accepted === true;
  const unlockedAfterAccepting = !accepted && Boolean(option.fields.hig_acceptedon);

  async function unlock() {
    setBusy(true);
    setError(null);
    try {
      await unlockOptionApi(option.id);
      onUnlocked();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  if (accepted) {
    return (
      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-900">
          <Lock className="h-4 w-4" />
          <span>Accepted on {day(option.fields.hig_acceptedon)} — locked. Its price is on the opportunity.</span>
          <button type="button" onClick={() => void unlock()} disabled={busy} className="ml-auto flex items-center gap-1 rounded-md border border-green-300 bg-white px-3 py-1 text-sm disabled:opacity-50">
            <LockOpen className="h-4 w-4" /> {busy ? 'Unlocking…' : 'Unlock to change'}
          </button>
        </div>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </div>
    );
  }
  if (unlockedAfterAccepting) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
        <LockOpen className="h-4 w-4" />
        Unlocked (accepted on {day(option.fields.hig_acceptedon)}). Changes aren&rsquo;t on the opportunity until you accept it again — Summary → Accept again (re-write the lines).
      </div>
    );
  }
  return null;
}
